;(function (global, factory) {
  const api = factory();

  global.ReturnsPortalHelpers = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const RETURN_SPECIFIC_REASON_MAP = {
    'item damaged / broken': 'damaged item',
    'wrong item sent': 'incorrect item',
  };
  const COLOR_OPTION_NAMES = ['color', 'colour'];
  const SIZE_OPTION_NAMES = ['size'];

  function trim(value) {
    return String(value || '').trim();
  }

  function normalizeLookupValue(value) {
    return trim(value).toLowerCase();
  }

  function toTitleHandle(value) {
    return trim(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function formatMoney(value) {
    const amount = Number(value);
    const normalized = Number.isFinite(amount) ? amount : 0;

    return '$' + normalized.toFixed(2);
  }

  function formatWarrantyTotal(value) {
    const amount = Number(value);
    const normalized = Number.isFinite(amount) ? amount : 0;
    const formatted = Number.isInteger(normalized)
      ? normalized.toFixed(0)
      : normalized.toFixed(2);

    return '$' + formatted + ' USD';
  }

  function parseMoney(value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value || '').replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(normalized);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  function clampCurrency(value) {
    const normalized = Number(value);

    if (!Number.isFinite(normalized) || normalized <= 0) {
      return 0;
    }

    return Number(normalized.toFixed(2));
  }

  function buildReasonId(mode, label, index) {
    return mode + '-reason-' + index + '-' + (toTitleHandle(label) || 'option');
  }

  function sanitizePortalStep(value) {
    const parsed = parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return 1;
    }

    if (parsed < 1) {
      return 1;
    }

    if (parsed > 4) {
      return 4;
    }

    return parsed;
  }

  function createPortalDraftKey(prefix, mode, orderNumber, zip) {
    const normalizedMode = trim(mode) || 'return';
    const normalizedOrderNumber = normalizeLookupValue(orderNumber);
    const normalizedZip = normalizeLookupValue(zip);

    if (!normalizedOrderNumber || !normalizedZip) {
      return '';
    }

    return (
      trim(prefix) +
      ':' +
      normalizedMode +
      ':' +
      normalizedOrderNumber +
      ':' +
      normalizedZip
    );
  }

  function isPortalDraftExpired(savedAt, now, ttlMs) {
    const normalizedSavedAt = Number(savedAt);
    const normalizedNow = Number(now);
    const normalizedTtl = Number(ttlMs);

    if (
      !Number.isFinite(normalizedSavedAt) ||
      !Number.isFinite(normalizedNow) ||
      !Number.isFinite(normalizedTtl) ||
      normalizedTtl <= 0
    ) {
      return true;
    }

    return normalizedNow - normalizedSavedAt > normalizedTtl;
  }

  function parseReturnReasons(rawReasons) {
    function parseFlag(value, fallback) {
      const normalized = trim(value).toLowerCase();

      if (!normalized) {
        return fallback;
      }

      if (['true', '1', 'yes'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no'].includes(normalized)) {
        return false;
      }

      return fallback;
    }

    return String(rawReasons || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line, index) => {
        const parts = line.split('|').map((part) => trim(part));
        const label = parts[0];
        const hasMedia = parseFlag(parts[1], false);
        const hasNotes = parseFlag(parts[2], true);

        return {
          id: buildReasonId('return', label, index),
          label: label,
          requiresMedia: hasMedia,
          requiresNotes: hasNotes,
          linkToWarranty: label.toLowerCase() === 'go to warranty page',
        };
      });
  }

  function buildReasons(mode, rawReasons) {
    if (mode === 'warranty') {
      return [
        {
          id: buildReasonId('warranty', 'I want to claim warranty', 0),
          label: 'I want to claim warranty',
          requiresMedia: true,
          requiresNotes: true,
          linkToWarranty: false,
        },
      ];
    }

    return parseReturnReasons(rawReasons);
  }

  function createReasonLookup(reasons) {
    return (reasons || []).reduce((lookup, reason) => {
      lookup[reason.id] = reason;
      return lookup;
    }, {});
  }

  function getReasonById(reasonsById, reasonId) {
    return (reasonsById || {})[reasonId] || null;
  }

  function getLineItemImage(lineItem) {
    return (
      lineItem?.variant?.image?.url ||
      lineItem?.product?.featuredImage?.url ||
      ''
    );
  }

  function getVariantOptions(lineItem) {
    const options = Array.isArray(lineItem?.variant?.selectedOptions)
      ? lineItem.variant.selectedOptions
      : [];

    return options
      .map((option) => ({
        name: trim(option?.name),
        value: trim(option?.value),
      }))
      .filter((option) => option.name && option.value);
  }

  function getVariantOptionValue(lineItem, allowedNames) {
    const option = getVariantOptions(lineItem).find((entry) =>
      allowedNames.includes(trim(entry?.name).toLowerCase())
    );

    return option?.value || '';
  }

  function getVariantColorValue(lineItem) {
    return getVariantOptionValue(lineItem, COLOR_OPTION_NAMES);
  }

  function getVariantSizeValue(lineItem) {
    return getVariantOptionValue(lineItem, SIZE_OPTION_NAMES);
  }

  function getVariantExtraOptions(lineItem) {
    return getVariantOptions(lineItem).filter((option) => {
      const normalizedName = trim(option?.name).toLowerCase();

      return (
        !COLOR_OPTION_NAMES.includes(normalizedName) &&
        !SIZE_OPTION_NAMES.includes(normalizedName)
      );
    });
  }

  function getLineItemQuantity(lineItem) {
    return Number(lineItem?.currentQuantity || lineItem?.quantity || 0);
  }

  function getLineItemUnitPrice(lineItem) {
    return parseMoney(lineItem?.discountedUnitPriceSet?.presentmentMoney?.amount);
  }

  function buildItemModel(lineItem, index) {
    const color = getVariantColorValue(lineItem);
    const size = getVariantSizeValue(lineItem);
    const variantOptions = getVariantExtraOptions(lineItem).map((option) => ({
      key: toTitleHandle(option.name + '-' + option.value),
      label: option.name,
      value: option.value,
    }));
    const variantSummary = []
      .concat(color ? ['Color: ' + color] : [])
      .concat(size ? ['Size: ' + size] : [])
      .concat(
        variantOptions.map((option) => option.label + ': ' + option.value)
      )
      .join(' / ');

    return {
      id: String(lineItem?.id || index),
      orderIndex: index + 1,
      lineItemId: String(lineItem?.id || index),
      productTitle: trim(lineItem?.title),
      titleHandle: toTitleHandle(lineItem?.title || 'item-' + (index + 1)),
      imageUrl: getLineItemImage(lineItem),
      sku: trim(lineItem?.variant?.sku),
      color: color,
      size: size,
      variantOptions: variantOptions,
      variantSummary: variantSummary,
      maxQuantity: getLineItemQuantity(lineItem),
      unitPrice: getLineItemUnitPrice(lineItem),
      returnable: Boolean(lineItem?.returnable),
      quantity: '',
      reasonId: '',
      reasonLabel: '',
      notes: '',
      media: [],
    };
  }

  function getOrderMeta(order) {
    const lineItems = Array.isArray(order?.lineItems) ? order.lineItems : [];

    return lineItems.reduce(
      (meta, lineItem) => {
        const quantity = getLineItemQuantity(lineItem);
        const unitPrice = getLineItemUnitPrice(lineItem);

        meta.orderItemsCount += quantity;
        meta.lineItemsSubtotal += unitPrice * quantity;

        return meta;
      },
      {
        orderItemsCount: 0,
        lineItemsSubtotal: 0,
        totalPrice: parseMoney(order?.currentTotalPriceSet?.presentmentMoney?.amount),
        taxAmount: parseMoney(order?.currentTotalTaxSet?.presentmentMoney?.amount),
      }
    );
  }

  function isItemEngaged(item) {
    return Boolean(
      item &&
        (
          item.reasonId ||
          item.quantity ||
          trim(item.notes) ||
          (Array.isArray(item.media) && item.media.length)
        )
    );
  }

  function isItemSelected(item) {
    return Boolean(item && item.reasonId && Number(item.quantity) > 0);
  }

  function getSelectedItems(items) {
    return (items || []).filter(isItemSelected);
  }

  function buildItemDisplayDetails(item, options) {
    const detailRows = [];

    function pushRow(key, label, value, config) {
      const normalizedValue = trim(value);

      if (!normalizedValue && normalizedValue !== '0') {
        return;
      }

      detailRows.push({
        key: key,
        label: label,
        value: normalizedValue,
        emphasis: Boolean(config?.emphasis),
      });
    }

    pushRow('sku', 'SKU', item?.sku);

    if (options?.includeQuantity) {
      pushRow(
        'quantity',
        options.quantityLabel || 'Quantity',
        String(options.quantityValue || item?.quantity || item?.maxQuantity || '')
      );
    }

    if (options?.includeUnitPrice !== false) {
      pushRow('unit-price', 'Unit price', formatMoney(item?.unitPrice));
    }

    if (options?.includeOrderTotal) {
      pushRow(
        'order-total',
        'Order total',
        formatMoney(Number(item?.maxQuantity) * Number(item?.unitPrice))
      );
    }

    if (options?.includeSelectedTotal) {
      pushRow(
        'selected-total',
        'Selected total',
        formatMoney(Number(item?.quantity) * Number(item?.unitPrice))
      );
    }

    return detailRows;
  }

  function getSuccessfulMedia(item) {
    return (item?.media || []).filter(
      (entry) => entry.status === 'success' && entry.remoteUrl
    );
  }

  function getPendingMedia(item) {
    return (item?.media || []).filter(
      (entry) => entry.status === 'uploading' || entry.status === 'queued'
    );
  }

  function getMediaCounts(item) {
    return getSuccessfulMedia(item).reduce(
      (counts, entry) => {
        const mimeType = trim(entry.type).toLowerCase();

        if (mimeType.indexOf('image/') === 0) {
          counts.images += 1;
        } else if (mimeType.indexOf('video/') === 0) {
          counts.videos += 1;
        } else {
          counts.other += 1;
        }

        return counts;
      },
      { images: 0, videos: 0, other: 0 }
    );
  }

  function getSelectedTotals(items) {
    return getSelectedItems(items).reduce(
      (totals, item) => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unitPrice);

        totals.quantity += quantity;
        totals.subtotal += quantity * unitPrice;

        return totals;
      },
      { quantity: 0, subtotal: 0 }
    );
  }

  function getMediaRequirement(mode, item, reason) {
    if (!reason || !reason.requiresMedia) {
      return {
        satisfied: true,
        message: '',
      };
    }

    const counts = getMediaCounts(item);

    if (mode === 'warranty') {
      const satisfied = counts.images >= 1 && counts.videos >= 1;

      return {
        satisfied: satisfied,
        message: satisfied
          ? ''
          : 'Upload at least 1 successful image and 1 successful video for this item before continuing.',
      };
    }

    const satisfied = getSuccessfulMedia(item).length >= 1;

    return {
      satisfied: satisfied,
      message: satisfied
        ? ''
        : 'Upload at least 1 successful photo or video for this item before continuing.',
    };
  }

  function getItemValidation(mode, item, reasonsById, options) {
    const reason = getReasonById(reasonsById, item?.reasonId);
    const errors = [];
    const engaged = isItemEngaged(item);
    const selected = isItemSelected(item);
    const notes = trim(item?.notes);
    const hasPendingUploads = getPendingMedia(item).length > 0;

    if (engaged && !item?.returnable) {
      errors.push('This item is not eligible for this request.');
    }

    if (item?.reasonId && !item?.quantity) {
      errors.push(
        'Choose the quantity you want to include for this item before continuing.'
      );
    }

    if (item?.quantity && !item?.reasonId) {
      errors.push(
        mode === 'warranty'
          ? 'Choose the warranty claim reason for this item before continuing.'
          : 'Choose a return reason for this item before continuing.'
      );
    }

    if (selected && hasPendingUploads) {
      errors.push(
        'Please wait for uploads to finish, or remove and retry any file that failed, before continuing.'
      );
    }

    if (selected && mode === 'warranty') {
      const minimumLength = Number(options?.warrantyDescriptionMinLength || 100);

      if (!notes || notes.length < minimumLength) {
        errors.push(
          'Add at least ' +
            minimumLength +
            ' characters describing the issue so our team can review the claim.'
        );
      }
    }

    if (selected) {
      const mediaRequirement = getMediaRequirement(mode, item, reason);

      if (!mediaRequirement.satisfied) {
        errors.push(mediaRequirement.message);
      }
    }

    return {
      engaged: engaged,
      selected: selected,
      reason: reason,
      errors: errors.filter(Boolean),
    };
  }

  function getStep2Validation(mode, items, reasonsById, options) {
    const itemErrors = {};
    let engagedCount = 0;
    let selectedCount = 0;
    let hasPendingUploads = false;

    (items || []).forEach((item) => {
      const validation = getItemValidation(mode, item, reasonsById, options);

      if (validation.engaged) {
        engagedCount += 1;
      }

      if (validation.selected) {
        selectedCount += 1;
      }

      if (validation.errors.length) {
        itemErrors[item.id] = validation.errors;
      }

      if (getPendingMedia(item).length) {
        hasPendingUploads = true;
      }
    });

    const canContinue =
      selectedCount > 0 &&
      engagedCount > 0 &&
      Object.keys(itemErrors).length === 0 &&
      !hasPendingUploads;

    return {
      canContinue: canContinue,
      engagedCount: engagedCount,
      selectedCount: selectedCount,
      itemErrors: itemErrors,
      hasPendingUploads: hasPendingUploads,
    };
  }

  function requiresManualReview(mode, items, reasonsById) {
    if (mode === 'warranty') {
      return getSelectedItems(items).length > 0;
    }

    return getSelectedItems(items).some((item) => {
      const reason = getReasonById(reasonsById, item.reasonId);
      return Boolean(reason?.requiresMedia);
    });
  }

  function getManualReviewHeading(config, selectedItems, reasonsById) {
    if (config?.mode !== 'return') {
      return config?.copy?.returnReasonGeneralHeading || '';
    }

    if (selectedItems.length !== 1) {
      return config?.copy?.returnReasonGeneralHeading || '';
    }

    const selectedReason = getReasonById(reasonsById, selectedItems[0].reasonId);
    const baseHeading = config?.copy?.returnReasonSpecificHeading || '';

    if (!selectedReason || baseHeading.indexOf('[RETURN_REASON]') === -1) {
      return config?.copy?.returnReasonGeneralHeading || '';
    }

    const normalizedReason =
      RETURN_SPECIFIC_REASON_MAP[selectedReason.label.toLowerCase()] ||
      selectedReason.label.toLowerCase();

    return baseHeading.replace('[RETURN_REASON]', normalizedReason);
  }

  function getShippingLabelText(config, shippingChoice) {
    if (shippingChoice === 'self') {
      return config?.shipping?.selfLabel || '';
    }

    if (shippingChoice === 'need') {
      return config?.shipping?.needLabel || '';
    }

    return '';
  }

  function mergePortalDraftItems(items, draftItems, reasonsById) {
    const draftLookup = draftItems || {};

    return (items || []).map((item) => {
      const draft =
        draftLookup[item.lineItemId] ||
        draftLookup[item.id] ||
        draftLookup[String(item.lineItemId)] ||
        draftLookup[String(item.id)];

      if (!draft) {
        return item;
      }

      const reason = getReasonById(reasonsById, draft.reasonId);
      const restoredMedia = Array.isArray(draft.media)
        ? draft.media
            .map((entry, index) => {
              const remoteUrl = trim(entry?.remoteUrl || entry?.previewUrl);

              if (!remoteUrl) {
                return null;
              }

              const type = trim(entry?.type) || 'application/octet-stream';
              const kind =
                trim(entry?.kind) === 'video' || type.toLowerCase().indexOf('video/') === 0
                  ? 'video'
                  : 'image';

              return {
                id: trim(entry?.id) || 'restored-media-' + item.id + '-' + index,
                name: trim(entry?.name) || 'Uploaded file',
                type: type,
                kind: kind,
                previewUrl: remoteUrl,
                remoteUrl: remoteUrl,
                status: 'success',
                error: '',
                file: null,
              };
            })
            .filter(Boolean)
        : [];

      item.quantity = draft.quantity ? String(draft.quantity) : '';
      item.reasonId = trim(draft.reasonId);
      item.reasonLabel = reason?.label || trim(draft.reasonLabel);
      item.notes = reason?.requiresNotes ? trim(draft.notes) : '';
      item.media = reason?.requiresMedia ? restoredMedia : [];

      return item;
    });
  }

  function computeReturnSummary(args) {
    const selectedTotals = getSelectedTotals(args?.items);
    const taxAmount = parseMoney(args?.taxAmount);
    const initialShipmentCost = parseMoney(args?.initialShipmentCost);
    const returnShipmentCost = 0;
    const baseShippingCost = parseMoney(args?.baseShippingCost);
    const makeInitialShippingZero = Boolean(args?.makeInitialShippingZero);
    const showShippingChoice = trim(args?.country) === 'United States';
    const shippingChoice = showShippingChoice
      ? args?.shippingChoice || 'need'
      : 'self';
    const grossRefund = selectedTotals.subtotal + taxAmount;
    const subtotal = grossRefund - baseShippingCost;
    const initialShipmentDisplayAmount =
      shippingChoice === 'need' && makeInitialShippingZero
        ? 0
        : initialShipmentCost;
    let refund = grossRefund;

    if (shippingChoice === 'self') {
      refund -= initialShipmentCost;
    }

    if (shippingChoice === 'need') {
      if (!makeInitialShippingZero) {
        refund -= initialShipmentCost;
      }

      refund -= returnShipmentCost;
    }

    refund -= baseShippingCost;

    return {
      mode: 'return',
      selectedQuantity: selectedTotals.quantity,
      selectedSubtotal: clampCurrency(selectedTotals.subtotal),
      taxAmount: clampCurrency(taxAmount),
      grossRefund: clampCurrency(grossRefund),
      subtotal: clampCurrency(subtotal),
      refund: clampCurrency(refund),
      initialShipmentCost: clampCurrency(initialShipmentDisplayAmount),
      returnShipmentCost: 0,
      baseShippingCost: clampCurrency(baseShippingCost),
      shippingChoice: shippingChoice,
      showShippingChoice: showShippingChoice,
      showSubtotalRow: true,
      showInitialShipmentCostRow:
        initialShipmentCost > 0 ||
        (shippingChoice === 'need' && makeInitialShippingZero),
      showShippingLabelCostRow: false,
      showBaseShippingCostRow: baseShippingCost > 0,
    };
  }

  function computeWarrantySummary(args) {
    const selectedTotals = getSelectedTotals(args?.items);
    const baseShippingCost = parseMoney(args?.baseShippingCost);
    const showShippingChoice = false;
    const shippingChoice = 'need';

    return {
      mode: 'warranty',
      selectedQuantity: selectedTotals.quantity,
      selectedSubtotal: clampCurrency(selectedTotals.subtotal),
      taxAmount: 0,
      grossRefund: 0,
      subtotal: 0,
      refund: 0,
      initialShipmentCost: 0,
      returnShipmentCost: 0,
      baseShippingCost: clampCurrency(baseShippingCost),
      shippingChoice: shippingChoice,
      showShippingChoice: showShippingChoice,
      showSubtotalRow: false,
      showInitialShipmentCostRow: false,
      showShippingLabelCostRow: false,
      showBaseShippingCostRow: baseShippingCost > 0,
    };
  }

  function buildRefundRows(summary) {
    if (summary?.mode === 'warranty') {
      return [
        {
          key: 'warranty-item-cost',
          label: 'Warranty Item cost',
          value: formatMoney(0),
        },
        {
          key: 'tax',
          label: 'Tax',
          value: formatMoney(summary?.taxAmount),
        },
        {
          key: 'warranty-shipping-cost',
          label: 'Warranty shipping cost',
          value: formatMoney(summary?.baseShippingCost),
        },
        {
          key: 'warranty-total',
          label: 'Total',
          value: formatWarrantyTotal(summary?.baseShippingCost),
          note: 'Will be charged after claim approval',
          emphasis: true,
        },
      ];
    }

    if (summary?.mode === 'return') {
      const returnRows = [
        {
          key: 'items',
          label: 'Refund Items (' + Number(summary?.selectedQuantity || 0) + ')',
          value: formatMoney(summary?.selectedSubtotal),
        },
        {
          key: 'tax',
          label: 'Tax',
          value: formatMoney(summary?.taxAmount),
        },
      ];

      if (summary?.showSubtotalRow) {
        returnRows.push({
          key: 'subtotal',
          label: 'Subtotal',
          value: formatMoney(summary?.subtotal),
        });
      }

      if (summary?.showInitialShipmentCostRow) {
        returnRows.push({
          key: 'initial-shipping',
          label: 'Initial Shipping Cost',
          value: formatMoney(summary?.initialShipmentCost),
        });
      }

      if (summary?.showBaseShippingCostRow) {
        returnRows.push({
          key: 'return-shipping',
          label: 'Return Shipping Cost',
          value: formatMoney(summary?.baseShippingCost),
        });
      }

      returnRows.push({
        key: 'estimated-refund',
        label: 'Est. Refund',
        value: formatMoney(summary?.refund),
        emphasis: true,
      });

      return returnRows;
    }

    const rows = [
      {
        key: 'items',
        label: 'Refund items (' + Number(summary?.selectedQuantity || 0) + ')',
        value: formatMoney(summary?.selectedSubtotal),
      },
      {
        key: 'tax',
        label: 'Tax',
        value: formatMoney(summary?.taxAmount),
      },
    ];

    if (summary?.showInitialShipmentCostRow) {
      rows.push({
        key: 'initial-shipping',
        label: 'Initial shipping cost',
        value: '-' + formatMoney(summary?.initialShipmentCost),
      });
    }

    if (summary?.showShippingLabelCostRow) {
      rows.push({
        key: 'shipping-label',
        label: 'Shipping label cost',
        value: '-' + formatMoney(summary?.returnShipmentCost),
      });
    }

    if (summary?.showBaseShippingCostRow) {
      rows.push({
        key: 'return-shipping',
        label: 'Return shipping cost',
        value: '-' + formatMoney(summary?.baseShippingCost),
      });
    }

    rows.push({
      key: 'estimated-refund',
      label: 'Estimated refund',
      value: formatMoney(summary?.refund),
      emphasis: true,
    });

    return rows;
  }

  function buildItemFieldName(prefix, item, suffix) {
    return (
      'contact[item_#' + item.orderIndex + '_' + prefix + '_' + item.titleHandle + ']'
    ).replace('__', '_');
  }

  function buildBaseFields(mode, state, summary, shippingLabelText) {
    const orderMeta = getOrderMeta(state.order);
    const fields = [
      { name: 'contact[email]', value: trim(state.order?.email) },
      { name: 'contact[item_total]', value: formatMoney(orderMeta.totalPrice) },
      { name: 'contact[tax_price]', value: formatMoney(orderMeta.taxAmount) },
      { name: 'contact[order_items_count]', value: String(orderMeta.orderItemsCount) },
      { name: 'contact[net_refund_value]', value: formatMoney(summary.refund) },
      { name: 'contact[shipping_label_details]', value: shippingLabelText || '' },
      { name: 'contact[cost_of_initial_shipping]', value: formatMoney(summary.initialShipmentCost) },
      { name: 'contact[request_id]', value: trim(state.requestId) },
    ];

    if (mode === 'warranty') {
      fields.push(
        {
          name: 'contact[warranty_items_count]',
          value: String(summary.selectedQuantity),
        },
        {
          name: 'contact[total_warranty_items_value]',
          value: formatMoney(summary.selectedSubtotal),
        },
        {
          name: 'contact[cost_of_warranty_label]',
          value: summary.returnShipmentCost ? formatMoney(summary.returnShipmentCost) : '',
        },
        {
          name: 'contact[warranty_shipping_cost]',
          value: formatMoney(summary.baseShippingCost),
        }
      );
    } else {
      fields.push(
        {
          name: 'contact[return_items_count]',
          value: String(summary.selectedQuantity),
        },
        {
          name: 'contact[total_return_items_value]',
          value: formatMoney(summary.selectedSubtotal),
        },
        {
          name: 'contact[return_shipping_cost]',
          value: formatMoney(summary.baseShippingCost),
        }
      );
    }

    return fields;
  }

  function buildItemFields(mode, state) {
    return getSelectedItems(state.items).flatMap((item) => {
      const successfulMedia = getSuccessfulMedia(item)
        .map((entry) => entry.remoteUrl)
        .join(', ');
      const fields = [
        {
          name: buildItemFieldName(
            mode === 'warranty' ? 'warranty_reason' : 'return_reason',
            item,
            ''
          ),
          value: trim(item.reasonLabel),
        },
        {
          name: buildItemFieldName(
            mode === 'warranty' ? 'warranty_quantity' : 'return_quantity',
            item,
            ''
          ),
          value: String(item.quantity),
        },
        {
          name: buildItemFieldName('refund_amount', item, ''),
          value: formatMoney(Number(item.quantity) * Number(item.unitPrice)),
        },
      ];

      if (trim(item.notes)) {
        fields.push({
          name: buildItemFieldName(
            mode === 'warranty' ? 'claim_reason' : 'other_reason',
            item,
            ''
          ),
          value: trim(item.notes),
        });
      }

      if (successfulMedia) {
        fields.push({
          name: buildItemFieldName(
            mode === 'warranty' ? 'warranty_product_images' : 'return_product_images',
            item,
            ''
          ),
          value: successfulMedia,
        });
      }

      return fields;
    });
  }

  function buildHiddenFields(mode, state, summary, config) {
    const shippingLabelText =
      mode === 'warranty' && !summary?.showShippingChoice
        ? ''
        : getShippingLabelText(config, summary.shippingChoice);

    return buildBaseFields(mode, state, summary, shippingLabelText)
      .concat(buildItemFields(mode, state))
      .filter((field) => trim(field.value) !== '');
  }

  return {
    buildHiddenFields: buildHiddenFields,
    buildItemDisplayDetails: buildItemDisplayDetails,
    buildItemModel: buildItemModel,
    buildRefundRows: buildRefundRows,
    buildReasons: buildReasons,
    clampCurrency: clampCurrency,
    createPortalDraftKey: createPortalDraftKey,
    computeReturnSummary: computeReturnSummary,
    computeWarrantySummary: computeWarrantySummary,
    createReasonLookup: createReasonLookup,
    formatMoney: formatMoney,
    getItemValidation: getItemValidation,
    getManualReviewHeading: getManualReviewHeading,
    getMediaCounts: getMediaCounts,
    getOrderMeta: getOrderMeta,
    getReasonById: getReasonById,
    getSelectedItems: getSelectedItems,
    getSelectedTotals: getSelectedTotals,
    getStep2Validation: getStep2Validation,
    isItemEngaged: isItemEngaged,
    isPortalDraftExpired: isPortalDraftExpired,
    mergePortalDraftItems: mergePortalDraftItems,
    normalizeLookupValue: normalizeLookupValue,
    parseMoney: parseMoney,
    parseReturnReasons: parseReturnReasons,
    requiresManualReview: requiresManualReview,
    sanitizePortalStep: sanitizePortalStep,
    toTitleHandle: toTitleHandle,
  };
});
